import { useState, useEffect, useCallback, useMemo } from 'react'
import { useAuth, useRealtimeSync } from '@structural-climbing/auth'

const apiUrl = import.meta.env.VITE_API_URL as string

/**
 * Whether the gym has a pending join request (the dot on Requests). The answer
 * comes from the Worker; the realtime channel only says when to ask again.
 */
export function useRealtimePendingRequests(gymId: string): boolean {
  const { isAuthenticated, getAccessTokenSilently } = useAuth()
  const [hasPending, setHasPending] = useState(false)

  const fetchCount = useCallback(async () => {
    if (!isAuthenticated) return
    try {
      const token = await getAccessTokenSilently()
      const res = await fetch(`${apiUrl}/join-requests/has-pending`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      if (!res.ok) return
      const { has_pending } = (await res.json()) as { has_pending?: boolean }
      setHasPending(has_pending === true)
    } catch {
      // Keep the last answer: a missed refresh is not a reason to hide the dot.
    }
  }, [isAuthenticated, getAccessTokenSilently])

  useEffect(() => {
    void fetchCount()
  }, [fetchCount, gymId])

  const bindings = useMemo(
    () => [{ table: 'join_requests', filter: `gym_id=eq.${gymId}` }],
    [gymId],
  )

  useRealtimeSync({
    channel: isAuthenticated ? `pending-requests-${gymId}` : null,
    bindings,
    onChange: () => void fetchCount(),
    onResync: () => void fetchCount(),
  })

  return hasPending
}
